import React from "react";
import { Box, Typography, Stack, Switch, TextField, MenuItem } from "@mui/material";

export type BaseUnit = "minutes" | "hours" | "days" | "weeks" | "months";

interface FrequencyConfigurationProps {
    useFrequency: boolean;
    setUseFrequency: (val: boolean) => void;
    baseInterval: number;
    setBaseInterval: (val: number) => void;
    baseUnit: BaseUnit;
    setBaseUnit: (val: BaseUnit) => void;
    setUseDayFilter: (val: boolean) => void;
    setUseDayOfMonthFilter: (val: boolean) => void;
    useMonthFilter: boolean;
    useDayFilter: boolean;
    useDayOfMonthFilter: boolean;
    setUseMonthFilter: (val: boolean) => void;
    primaryColor: string;
    primaryLightColor: string;
    secondaryColor: string;
    panelBg: string;
    borderColor: string;
    stepDotStyle: (isActive: boolean) => React.CSSProperties;
    UNIT_LABELS: Record<string, string>;
    getTranslation: (key: string) => string;
}

const FrequencyConfiguration: React.FC<FrequencyConfigurationProps> = ({
    useFrequency,
    setUseFrequency,
    baseInterval,
    setBaseInterval,
    baseUnit,
    setBaseUnit,
    setUseDayFilter,
    setUseDayOfMonthFilter,
    useMonthFilter,
    useDayFilter,
    useDayOfMonthFilter,
    setUseMonthFilter,
    primaryColor,
    primaryLightColor,
    secondaryColor,
    panelBg,
    borderColor,
    stepDotStyle,
    UNIT_LABELS,
    getTranslation,
}) => {
    return (
        <Box
            sx={{
                position: "relative",
                borderLeft: `2px solid ${primaryLightColor}`,
                pl: 4,
            }}
        >
            <Box sx={stepDotStyle(true)} />
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Typography
                    variant="caption"
                    fontWeight={700}
                    color={secondaryColor}
                    sx={{ letterSpacing: 1 }}
                >
                    {getTranslation("pm.timer_config.section.frequency")}
                </Typography>
                <Switch
                    size="small"
                    checked={useFrequency}
                    onChange={(e) => {
                        const newValue = e.target.checked;
                        if (newValue) {
                            setUseFrequency(true);
                        } else {
                            setUseFrequency(false);
                            // Se spegnendo questo lasciamo 'Mesi' da solo, riaccendiamo DOM
                            if (useMonthFilter && !useDayFilter && !useDayOfMonthFilter) {
                                setUseDayOfMonthFilter(true);
                            }
                        }
                    }}
                    sx={{
                        "& .MuiSwitch-switchBase.Mui-checked": {
                            color: primaryColor,
                        },
                        "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                            backgroundColor: primaryColor,
                        },
                    }}
                />
            </Stack>

            <Stack
                direction="row"
                alignItems="center"
                spacing={3}
                sx={{
                    mt: 1.5,
                    p: 3,
                    borderRadius: "10px",
                    backgroundColor: useFrequency ? panelBg : "action.disabledBackground",
                    border: `1px solid ${borderColor}`,
                    opacity: useFrequency ? 1 : 0.6,
                    pointerEvents: useFrequency ? "auto" : "none",
                    transition: "all 0.2s",
                }}
            >
                <Typography
                    variant="body2"
                    color={secondaryColor}
                    fontWeight={500}
                >
                    {getTranslation("pm.timer_config.label.repeat_every")}
                </Typography>
                <TextField
                    type="number"
                    size="small"
                    inputProps={{ min: 1, style: { textAlign: "center" } }}
                    value={baseInterval}
                    onChange={(e) =>
                        setBaseInterval(Math.max(1, parseInt(e.target.value) || 1))
                    }
                    sx={{
                        width: 70,
                        "& .MuiOutlinedInput-root": {
                            fontWeight: 500,
                            fontSize: "1rem",
                            color: primaryColor,
                            backgroundColor: "background.paper",
                            "& fieldset": { borderColor: primaryColor },
                        },
                    }}
                />
                <TextField
                    select
                    size="small"
                    value={baseUnit}
                    onChange={(e) => setBaseUnit(e.target.value as BaseUnit)}
                    sx={{
                        minWidth: 100,
                        "& .MuiOutlinedInput-root": {
                            fontWeight: 500,
                            fontSize: "1rem",
                            backgroundColor: "background.paper",
                            "& fieldset": { borderColor: primaryColor },
                        },
                    }}
                >
                    <MenuItem value="minutes">{UNIT_LABELS.minutes}</MenuItem>
                    <MenuItem value="hours">{UNIT_LABELS.hours}</MenuItem>
                    <MenuItem value="days">{UNIT_LABELS.days}</MenuItem>
                    <MenuItem value="weeks">{UNIT_LABELS.weeks}</MenuItem>
                    <MenuItem value="months">{UNIT_LABELS.months}</MenuItem>
                </TextField>
            </Stack>
        </Box>
    );
};

export default FrequencyConfiguration;
